import { useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SectionCard, fmtDay } from '../../shared/Panel';
import { ClampedText } from '../../shared/listCells';
import { plural } from '../../shared/plural';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  ACTIVITY_CHOICES,
  activityFromChoice,
  activityLabel,
  activityToChoice,
  addCarrierContact,
  deleteCarrierContact,
  fetchContactOwners,
  updateCarrierContact,
  updateCarrierProfile,
  type CarrierContact,
  type ContactInput,
  type Owner,
} from './carriersApi';
import { recordScoped } from '../../shared/recordScoped';
import { formatPricePerKm, type PricePerKm } from './pricePerKm';
import { ContactKindChip } from '../contractors/cells';

type LooseRecord = RaRecord & Record<string, unknown>;

interface Profile {
  orderCount: number;
  lastOrderAt: string | null;
  vehicles: Array<{ number: string; orders: number; lastSeen: string }>;
  /** `pricePerKm` od 06.10.2026 — starsza odpowiedź może go nie mieć. */
  directions: Array<{ from: string; to: string; orders: number; pricePerKm?: PricePerKm }>;
}

/**
 * Profil z historii zleceń (I.07) — liczy API z danych TMS: pojazdy, którymi
 * przewoźnik jeździł dla Marathona, i kierunki. To ślad, nie kartoteka jego
 * floty.
 */
export const HistoryProfile = () => {
  const r = useRecordContext<LooseRecord>();
  const p = r?.profile as Profile | undefined;
  if (!r || !p) return null;
  return (
    <SectionCard title="Profil z historii zleceń">
      {p.orderCount === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak zleceń, więc nie ma z czego złożyć profilu.
        </Typography>
      ) : (
        <Stack spacing={1}>
          <Typography variant="body2">
            {plural(p.orderCount, 'zlecenie', 'zlecenia', 'zleceń')}, ostatnie{' '}
            {fmtDay(p.lastOrderAt)}
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Kierunki (kraj załadunku → rozładunku):
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5 }} useFlexGap>
              {p.directions.length === 0 && <Typography variant="caption">—</Typography>}
              {/* Cena za km na kierunku (06.10.2026) — ta sama reguła co kolumna „$”
                  przy zleceniu; bez ceny chip zostaje taki jak dotąd. */}
              {p.directions.map((d) => (
                <Chip
                  key={`${d.from}-${d.to}`}
                  size="small"
                  label={
                    `${d.from} → ${d.to}: ${d.orders}` +
                    (d.pricePerKm?.pln != null ? ` · ${formatPricePerKm(d.pricePerKm)}` : '')
                  }
                  title={d.pricePerKm?.description ?? undefined}
                />
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Pojazdy w zleceniach dla Marathona (numer z TMS):
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5 }} useFlexGap>
              {p.vehicles.length === 0 && (
                <Typography variant="caption">TMS nie podał numerów pojazdów</Typography>
              )}
              {p.vehicles.map((v) => (
                <Chip
                  key={v.number}
                  size="small"
                  variant="outlined"
                  label={`${v.number} · ${v.orders}× · ${fmtDay(v.lastSeen)}`}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      )}
    </SectionCard>
  );
};

/** NIP/VAT, aktywność i notatki — uzupełnia Trade; TMS ich nie podaje. */
const CarrierDetailsPanel = () => {
  const r = useRecordContext<LooseRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [editing, setEditing] = useState(false);
  const [taxId, setTaxId] = useState('');
  const [active, setActive] = useState<'true' | 'false' | ''>('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  if (!r) return null;

  const start = () => {
    setTaxId(String(r.taxId ?? ''));
    setActive(activityToChoice(r.active));
    setNotes(String(r.notes ?? ''));
    setEditing(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await updateCarrierProfile(r.id, {
        taxId: taxId.trim() || null,
        active: activityFromChoice(active),
        notes: notes.trim() || null,
      });
      notify('Zapisano dane przewoźnika', { type: 'success' });
      setEditing(false);
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="Dane przewoźnika">
      {!editing ? (
        <Stack spacing={0.5}>
          <Typography variant="body2">NIP/VAT: {String(r.taxId ?? '—')}</Typography>
          <Typography variant="body2">Współpraca: {activityLabel(r.active)}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            Notatki: {String(r.notes ?? '—')}
          </Typography>
          <Box>
            <Button size="small" onClick={start}>
              Edytuj
            </Button>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={1}>
          <TextField size="small" label="NIP/VAT" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
          <TextField
            select
            size="small"
            label="Współpraca"
            value={active}
            onChange={(e) => setActive(e.target.value as 'true' | 'false' | '')}
          >
            {ACTIVITY_CHOICES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Notatki"
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={() => void save()} disabled={busy}>
              Zapisz
            </Button>
            <Button size="small" onClick={() => setEditing(false)} disabled={busy}>
              Anuluj
            </Button>
          </Stack>
        </Stack>
      )}
    </SectionCard>
  );
};

const EMPTY_CONTACT: ContactInput = {
  name: '',
  role: '',
  email: '',
  phone: '',
  channel: '',
  ownerUserId: null,
};

/** Kontakty: osoba, rola, e-mail, telefon, kanał i opiekun po stronie Marathona. */
const CarrierContactsPanel = () => {
  const r = useRecordContext<LooseRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [owners, setOwners] = useState<Owner[]>([]);
  /** `null` = okno zamknięte; `0` = nowy kontakt; inaczej id edytowanego. */
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ContactInput>(EMPTY_CONTACT);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchContactOwners()
      .then(setOwners)
      .catch(() => setOwners([]));
  }, []);

  if (!r) return null;
  const contacts = (r.contacts as CarrierContact[] | undefined) ?? [];

  const open = (c?: CarrierContact) => {
    setForm(
      c
        ? {
            name: c.name ?? '',
            role: c.role ?? '',
            email: c.email ?? '',
            phone: c.phone ?? '',
            channel: c.channel ?? '',
            ownerUserId: c.owner?.id ?? null,
          }
        : EMPTY_CONTACT,
    );
    setEditingId(c ? c.id : 0);
  };
  const set = (k: keyof ContactInput) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      notify(success, { type: 'success' });
      setEditingId(null);
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const payload = (): ContactInput => ({
    name: form.name || null,
    role: form.role || null,
    email: form.email || null,
    phone: form.phone || null,
    channel: form.channel || null,
    ownerUserId: form.ownerUserId ?? null,
  });

  return (
    <SectionCard title={`Kontakty (${contacts.length})`}>
      {contacts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak kontaktów wpisanych przez Trade. Kontakty z TMS są w osobnej sekcji niżej.
        </Typography>
      ) : (
        // Siedem kolumn na karcie przewoźnika: nagłówki i telefon w jednej
        // linii, dłuższe teksty skracane z podpowiedzią (28.09.2026).
        <Table size="small" sx={{ '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Osoba</TableCell>
              <TableCell>Rola</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Kanał</TableCell>
              <TableCell>Opiekun</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <ClampedText text={c.name} maxWidth={220} />
                </TableCell>
                <TableCell>
                  <ClampedText text={c.role} maxWidth={160} />
                </TableCell>
                <TableCell>
                  <ClampedText text={c.email} maxWidth={240} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.phone ?? '—'}</TableCell>
                <TableCell>{c.channel ?? '—'}</TableCell>
                <TableCell>{c.owner?.username ?? '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => open(c)}>
                    Edytuj
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    disabled={busy}
                    onClick={() => void run(() => deleteCarrierContact(r.id, c.id), 'Usunięto kontakt')}
                  >
                    Usuń
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={{ mt: 1 }}>
        <Button size="small" onClick={() => open()}>
          Dodaj kontakt
        </Button>
      </Box>

      <Dialog open={editingId !== null} onClose={() => !busy && setEditingId(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Kontakt przewoźnika' : 'Nowy kontakt przewoźnika'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <TextField size="small" label="Osoba" value={form.name ?? ''} onChange={set('name')} />
            <TextField size="small" label="Rola (np. dyspozytor, właściciel)" value={form.role ?? ''} onChange={set('role')} />
            <TextField size="small" label="E-mail" value={form.email ?? ''} onChange={set('email')} />
            <TextField size="small" label="Telefon" value={form.phone ?? ''} onChange={set('phone')} />
            <TextField size="small" label="Kanał (np. telefon, e-mail, giełda)" value={form.channel ?? ''} onChange={set('channel')} />
            <TextField
              select
              size="small"
              label="Opiekun po stronie Marathona"
              value={form.ownerUserId ?? ''}
              onChange={(e) =>
                setForm({ ...form, ownerUserId: e.target.value === '' ? null : Number(e.target.value) })
              }
            >
              <MenuItem value="">— brak —</MenuItem>
              {owners.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.username}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingId(null)} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() =>
              void run(
                () =>
                  editingId
                    ? updateCarrierContact(r.id, editingId, payload())
                    : addCarrierContact(r.id, payload()),
                editingId ? 'Zapisano kontakt' : 'Dodano kontakt',
              )
            }
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

/** Kontakt przewoźnika z TMS — kształt `tmsContacts` z `GET /carriers/:id`. */
export interface TmsContact {
  id: number;
  corrName: string | null;
  email: string | null;
  phone: string | null;
  typeOfActivity: string | null;
}

/**
 * Kontakty przewoźnika z TMS (odpowiedzi Marathonu/Euvic 16.10.2026,
 * pyt. 56): e-maile przewoźników są w kartotece kontrahentów TMS, wspólnej
 * dla klientów i przewoźników. Tylko odczyt, z oznaczeniem „z TMS”, obok
 * kontaktów wpisanych przez Trade; zmienia się je w TMS. Rodzaj — etykieta
 * po polsku z kodu (pyt. 33e).
 */
export const CarrierTmsContacts = () => {
  const r = useRecordContext<LooseRecord>();
  if (!r) return null;
  const contacts = (r.tmsContacts as TmsContact[] | undefined) ?? [];
  return (
    <SectionCard title={`Kontakty z TMS (${contacts.length})`}>
      {contacts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          TMS nie podaje kontaktów tego przewoźnika.
        </Typography>
      ) : (
        <Table size="small" sx={{ '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Osoba</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Rodzaj</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <ClampedText text={c.corrName} maxWidth={220} />
                </TableCell>
                <TableCell>
                  <ClampedText text={c.email} maxWidth={260} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.phone ?? '—'}</TableCell>
                <TableCell>
                  <ContactKindChip kod={c.typeOfActivity} />
                </TableCell>
                <TableCell align="right">
                  <Chip size="small" variant="outlined" label="z TMS" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
        Tylko odczyt — kontakty z kartoteki kontrahentów w TMS (ten sam numer co przewoźnik). Zmienia się je
        w TMS; można je wybrać na adresata zapytania do przewoźnika.
      </Typography>
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const CarrierDetails = recordScoped(CarrierDetailsPanel);

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const CarrierContacts = recordScoped(CarrierContactsPanel);
